import { SUPPORT_EMAIL } from "constants/contactInfo";
import { ProcedureRow, ProviderDropdown } from "types/CreateOrder";

/**
 * CLE-3074 — on a Finix account, a provider with no Finix merchant account cannot be paid, so the API
 * refuses (400) an estimate that would pay one. These helpers let the page say so before submit. The
 * API is the source of truth; this is only an early warning, and only on Finix accounts (on Stripe a
 * provider with no Connect account is paid into Clear Health's own on purpose).
 *
 * A provider is "unlinked" only when its `finixMerchantId` is KNOWN and blank. A provider object
 * with no `finixMerchantId` key (a row rehydrated from a saved order before the provider search
 * landed) is unknown and never flagged — the API decides.
 */

/** A form field, like `pendingEligibilityRuns`: `mapFormToRequestBody` maps by name, so it is never sent. */
export const UNLINKED_PAYOUT_PROVIDERS_FIELD = "unlinkedPayoutProviderNames";

export const PAYMENT_SETUP_INCOMPLETE_SUFFIX = " — payment setup incomplete";

/** The API's message (orders/order.helpers.ts `providerPaymentSetupIncompleteMessage`), word for word. */
export const providerPaymentSetupIncompleteMessage = (
  providerName: string
): string =>
  `The payment processing setup for ${providerName} provider is not complete. Please reach out to Clear Health at ${SUPPORT_EMAIL}.`;

/**
 * Whether an account takes payments on Finix, from an account object already in hand (the order's
 * `account`, which the order read includes in full). For pages that own their form and so cannot use
 * `useIsFinixAccount`.
 */
export const isFinixAccountOf = (
  account: { features?: { useFinix?: boolean } | null } | null | undefined
): boolean => account?.features?.useFinix === true;

export const hasFinixMerchant = (
  finixMerchantId: string | null | undefined
): boolean =>
  typeof finixMerchantId === "string" && finixMerchantId.trim() !== "";

export const isPaymentSetupIncomplete = (
  provider: { finixMerchantId?: string | null } | null | undefined
): boolean =>
  !!provider &&
  "finixMerchantId" in provider &&
  !hasFinixMerchant(provider.finixMerchantId);

/**
 * The provider a new row starts on: on a Finix account the first one whose payment setup is
 * complete, otherwise (or when none is) the first one.
 */
export const pickDefaultPayoutProvider = (
  providerDropdown: ProviderDropdown[],
  isFinixAccount: boolean
): ProviderDropdown | undefined =>
  (isFinixAccount
    ? providerDropdown.find((provider) => !isPaymentSetupIncomplete(provider))
    : undefined) ?? providerDropdown[0];

/** The selected providers whose payment setup is incomplete, by name, once each, in row order. */
export const getUnlinkedPayoutProviderNames = (
  rows: Pick<ProcedureRow, "selectedProvider">[] | null | undefined,
  isFinixAccount: boolean
): string[] => {
  if (!isFinixAccount || !rows?.length) return [];
  const names = rows
    .map((row) => row?.selectedProvider)
    .filter((provider) => isPaymentSetupIncomplete(provider))
    .map((provider) => provider.name);
  return Array.from(new Set(names));
};

/**
 * Gives every provider on the rows the `finixMerchantId` the provider search returned for it. The
 * Review / Redo / Copy pages rebuild rows from the saved order, whose providers carry no merchant id;
 * the search results do, and a merchant id belongs to the provider, not to the procedure.
 */
export const enrichRowsWithFinixMerchantIds = <T extends ProcedureRow>(
  rows: T[],
  providerProcedures:
    | { provider?: { id?: string; finixMerchantId?: string | null } }[]
    | null
    | undefined
): T[] => {
  const merchantIdByProviderId = new Map<string, string | null>();
  (providerProcedures ?? []).forEach((providerProcedure) => {
    const provider = providerProcedure?.provider;
    if (provider?.id && "finixMerchantId" in provider) {
      merchantIdByProviderId.set(provider.id, provider.finixMerchantId ?? null);
    }
  });
  if (merchantIdByProviderId.size === 0) return rows;
  const enrich = (provider: ProviderDropdown): ProviderDropdown =>
    provider && merchantIdByProviderId.has(provider.id)
      ? {
          ...provider,
          finixMerchantId: merchantIdByProviderId.get(provider.id) ?? null,
        }
      : provider;
  return rows.map((row) => ({
    ...row,
    selectedProvider: enrich(row.selectedProvider),
    providerDropdown: (row.providerDropdown ?? []).map(enrich),
  }));
};
